import React from "react";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import { NewspaperOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useGeneralNews, GeneralNewsArticle } from "../../../hooks/useGeneralNews";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { hapticSelection } from "../../../lib/haptics";
import {
  focusRingSx,
  panelBorder,
  panelSurface,
  panelTitleSx,
  sectionHeaderSx,
} from "../overviewPanel";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

// The news column: a floating header in the band's title idiom, then an
// editorial front page in open cards, one story per row. The newest story
// LEADS (photo on the left at a size newswire art can actually fill, text
// on the right) and the stories beneath fall into compact thumbnail cards.
// Wire art is optional — FMP sends `image: null` for many fresh stories —
// so a story without a photo simply gives its words the full width; there
// is no placeholder tile. The size break is what makes it feel like a
// publication deciding what matters; the shared card chrome and header
// line are what keep it consistent with the watchlist beside it.

const VISIBLE_ARTICLES = 10;
const IMAGE_W = 108;
const IMAGE_H = 72;

const publishedFromNow = (article: GeneralNewsArticle): string => {
  try {
    // FMP timestamps are ET wall-clock; dayjs.tz handles EST/EDT.
    return dayjs.tz(article.publishedDate, "America/New_York").fromNow();
  } catch {
    return dayjs(article.publishedDate).fromNow();
  }
};

// A photo URL FMP hands us can still 404; the story then reads as text
// only rather than showing the browser's broken-image glyph.
const hideBrokenImage = (event: React.SyntheticEvent<HTMLImageElement>) => {
  event.currentTarget.style.display = "none";
};

// Shared story chrome — every story sits on its own card on the band's
// surface, the same chrome and green "tap in" gesture as the watchlist
// cards beside it: the wash warms, the border and headline light up, the
// artwork comes to full brightness, and the press dips the card.
const useRowSx = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return {
    textDecoration: "none",
    borderRadius: 1.5,
    backgroundColor: panelSurface(isDark),
    border: `1px solid ${panelBorder(isDark)}`,
    WebkitTapHighlightColor: "transparent",
    transition: "background-color 0.2s ease, border-color 0.2s ease, transform 0.15s ease",
    "&:hover": {
      backgroundColor: isDark ? "rgba(34, 197, 94, 0.05)" : "rgba(34, 197, 94, 0.04)",
      borderColor: "rgba(34, 197, 94, 0.45)",
      "& .news-art": { opacity: 1 },
      "& .news-headline": { color: "#22c55e" },
    },
    "&:active": { transform: "scale(0.98)" },
  } as const;
};

const SourceAndAge: React.FC<{ article: GeneralNewsArticle }> = ({ article }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: "0.625rem",
          fontWeight: 600,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: "#22c55e",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {article.site}
      </Typography>
      <Typography
        sx={{ fontSize: "0.625rem", color: theme.palette.text.secondary, whiteSpace: "nowrap", flexShrink: 0 }}
      >
        {publishedFromNow(article)}
      </Typography>
    </Box>
  );
};

// The front-page lead: newest story, biggest treatment — a split card with
// the photo holding the left at a size the source art fills honestly (no
// upscale, no blur workarounds), and the words holding the right.
const NewsLead: React.FC<{ article: GeneralNewsArticle }> = ({ article }) => {
  const theme = useTheme();
  const { trackFeature } = useAnalytics();
  const rowSx = useRowSx();

  return (
    <Box
      component="a"
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        hapticSelection();
        trackFeature("news_list_article_opened", { site: article.site, lead: true });
      }}
      sx={{ ...rowSx, display: "flex", alignItems: "stretch", gap: 1.75, p: 1.5 }}
    >
      {article.image && (
        <Box
          component="img"
          className="news-art"
          src={article.image}
          alt=""
          onError={hideBrokenImage}
          sx={{
            width: "44%",
            minHeight: 180,
            maxHeight: 240,
            borderRadius: 1,
            objectFit: "cover",
            flexShrink: 0,
            opacity: 0.92,
            transition: "opacity 0.2s ease",
          }}
        />
      )}
      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          py: 0.5,
        }}
      >
        <SourceAndAge article={article} />
        <Typography
          className="news-headline"
          sx={{
            mt: 1,
            fontSize: "1rem",
            fontWeight: 700,
            lineHeight: 1.35,
            transition: "color 0.15s ease",
            color: theme.palette.text.primary,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {article.title}
        </Typography>
        {article.text && (
          <Typography
            sx={{
              mt: 1,
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              color: theme.palette.text.secondary,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {article.text}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// The compact cards beneath the lead: thumbnail left when the story has
// one, headline right.
const NewsRow: React.FC<{ article: GeneralNewsArticle }> = ({ article }) => {
  const theme = useTheme();
  const { trackFeature } = useAnalytics();
  const rowSx = useRowSx();

  return (
    <Box
      component="a"
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        hapticSelection();
        trackFeature("news_list_article_opened", { site: article.site });
      }}
      sx={{ ...rowSx, display: "flex", alignItems: "stretch", gap: 1.5, p: 1.5 }}
    >
      {article.image && (
        <Box
          component="img"
          className="news-art"
          src={article.image}
          alt=""
          loading="lazy"
          onError={hideBrokenImage}
          sx={{
            width: IMAGE_W,
            height: IMAGE_H,
            borderRadius: 1,
            objectFit: "cover",
            flexShrink: 0,
            alignSelf: "center",
            opacity: 0.88,
            transition: "opacity 0.2s ease",
          }}
        />
      )}

      <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Box sx={{ mb: 0.5 }}>
          <SourceAndAge article={article} />
        </Box>
        <Typography
          className="news-headline"
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            lineHeight: 1.35,
            transition: "color 0.15s ease",
            color: theme.palette.text.primary,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {article.title}
        </Typography>
        {article.text && (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: "0.75rem",
              lineHeight: 1.4,
              color: theme.palette.text.secondary,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {article.text}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const NewsList: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { data, isLoading, error, refetch } = useGeneralNews();

  const articles = (data?.data.articles ?? []).slice(0, VISIBLE_ARTICLES);
  const [lead, ...rest] = articles;
  const skeletonBg = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.09)";

  // A failed first load says so, with a way back; a poll that fails after a
  // successful one keeps the last front page rather than blanking it. An
  // empty wire is named too, so neither reads as a missing column.
  const failed = !isLoading && error != null && data === undefined;
  const empty = !isLoading && !failed && articles.length === 0;

  const quietCardSx = {
    minHeight: 160,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    px: 2,
    py: 3,
    borderRadius: 1.5,
    backgroundColor: panelSurface(isDark),
    border: `1px solid ${panelBorder(isDark)}`,
  } as const;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      {/* Floating header, on the same optical line as the watchlist's */}
      <Box sx={sectionHeaderSx}>
        <NewspaperOutlined sx={{ fontSize: 15, color: "#22c55e", flexShrink: 0 }} />
        <Typography
          sx={{
            ...panelTitleSx,
            color: theme.palette.text.secondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Latest news
        </Typography>
      </Box>

      {isLoading && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Skeleton
            variant="rounded"
            animation="wave"
            height={216}
            sx={{ borderRadius: 1.5, bgcolor: skeletonBg }}
          />
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton
              key={index}
              variant="rounded"
              animation="wave"
              height={IMAGE_H + 24}
              sx={{ borderRadius: 1.5, bgcolor: skeletonBg }}
            />
          ))}
        </Box>
      )}

      {failed && (
        <Box sx={quietCardSx}>
          <NewspaperOutlined sx={{ fontSize: 22, color: theme.palette.text.disabled }} />
          <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
            Couldn&apos;t load the news
          </Typography>
          <Box
            component="button"
            onClick={() => refetch()}
            sx={{
              all: "unset",
              ...focusRingSx,
              boxSizing: "border-box",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#22c55e",
              minHeight: 44,
              px: 2,
              WebkitTapHighlightColor: "transparent",
              transition: "opacity 0.2s ease",
              "&:hover": { opacity: 0.8 },
              "&:active": { opacity: 0.6 },
            }}
          >
            Retry
          </Box>
        </Box>
      )}

      {empty && (
        <Box sx={quietCardSx}>
          <NewspaperOutlined sx={{ fontSize: 22, color: theme.palette.text.disabled }} />
          <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
            No stories right now
          </Typography>
        </Box>
      )}

      {!isLoading && !failed && !empty && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {lead && <NewsLead article={lead} />}
          {rest.map((article, index) => (
            <NewsRow key={`${article.url}-${index}`} article={article} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default NewsList;
